"use client";

import { api } from "@/trpc/react";
import { type api as serverApi } from "@/trpc/server";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";

type PayloadType = Parameters<
  typeof serverApi.supplierAccountsRouter.logActivity
>[0];

type ActionQueueContextValue = (payload: PayloadType) => void;

const ActionQueueContext = createContext<ActionQueueContextValue | undefined>(
  undefined,
);

export const ActionQueueProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const queueRef = useRef<PayloadType[]>([]);
  const isProcessingRef = useRef(false);
  const mutation = api.supplierAccountsRouter.logActivity.useMutation();

  /**
   * Process the next item in the queue
   */
  const processNext = useCallback(() => {
    const next = queueRef.current.shift();
    if (!next) {
      isProcessingRef.current = false;
      return;
    }

    void (async () => {
      try {
        /**
         * We need to use sync mutate, because the async version
         * will make the top level handler wait until the async is done
         * and the route.push won't trigger until the handler has been resolved.
         */
        mutation.mutate(next);
      } catch (error) {
        console.error("Failed to log action:", error);
      } finally {
        isProcessingRef.current = false;
        setTimeout(() => processNext(), 500);
      }
    })();
  }, [mutation]);

  const value = useCallback(
    (payload: PayloadType) => {
      queueRef.current.push(payload);
      if (!isProcessingRef.current) {
        isProcessingRef.current = true;
        setTimeout(() => processNext(), 500);
      }
    },
    [processNext],
  );

  return (
    <ActionQueueContext.Provider value={value}>
      {children}
    </ActionQueueContext.Provider>
  );
};

export const useActionQueueContext = () => {
  const context = useContext(ActionQueueContext);

  if (!context)
    throw new Error(
      `You cannot call useActionQueueContext outside of the ActionQueueContext.`,
    );

  return context;
};
